import React from 'react';
import { CustomIcon } from '../CustomIcon/CustomIcon';
import { CustomIconName } from '../CustomIcon/CustomIcon';
import { dispatchPopupEvent, normalizePopupId } from '../../utils/popupEvents';
import type { CtaBehavior } from '../../utils/ctaBehavior';
import type { MegaMenuCtaAction } from './MegaMenu';
import { CTA_PREFIXES, useClickTracking } from '../../hooks/useClickTracking';

/** Props for the {@link MegaMenuCta} button/link pivot. */
export interface MegaMenuCtaProps {
  /** Visible label text. */
  text: string;
  /** Navigation URL, ignored when Action is Popup. */
  url?: string;
  /** CSS class override. @default 'poly-mega-menu__drawer-footer-btn' */
  className?: string;
  /** Analytics CTA identifier (`data-cta-id`). */
  ctaId?: string;
  /** Link-decoration appendix (`data-ld-append`). */
  ldAppendix?: string;
  /** Same-tab or new-tab link behavior. */
  behavior?: CtaBehavior;
  /** Link or Popup action. */
  action?: MegaMenuCtaAction;
  /** Popup ID dispatched when Action is Popup. */
  popupTargetId?: string;
  /** Optional leading icon name from {@link CustomIcon}. */
  iconName?: CustomIconName;
}

/**
 * MegaMenuCta — Shared button/link pivot for MegaMenu drawer footer.
 *
 * Renders a `<button>` for the Popup action or an `<a>` for navigation,
 * with optional leading icon. Captures the shared pivot logic that was
 * previously duplicated for primary and secondary CTAs.
 */
export const MegaMenuCta: React.FC<MegaMenuCtaProps> = ({
  text, url, className = 'poly-mega-menu__drawer-footer-btn',
  ctaId, ldAppendix, behavior, action = 'link', popupTargetId, iconName,
}) => {
  const icon = iconName && <CustomIcon name={iconName} size="1.25em" aria-hidden="true" />;
  const trackClick = useClickTracking(CTA_PREFIXES.nav, {
    ctaId,
    href: action === 'popup' ? undefined : url,
    label: action === 'popup' ? normalizePopupId(popupTargetId) : text,
  });

  if (action === 'popup') {
    return (
      <button
        type="button"
        className={className}
        data-cta-id={ctaId}
        data-ld-append={ldAppendix}
        onClick={(e) => {
          trackClick();
          dispatchPopupEvent(e.currentTarget, popupTargetId ?? '');
        }}
      >
        {icon}
        <span>{text}</span>
      </button>
    );
  }

  return (
    <a
      href={url}
      className={className}
      data-cta-id={ctaId}
      data-ld-append={ldAppendix}
      target={behavior === 'newTab' ? '_blank' : undefined}
      rel={behavior === 'newTab' ? 'noopener noreferrer' : undefined}
      onClick={trackClick}
    >
      {icon}
      <span>{text}</span>
    </a>
  );
};
